import type { AppRoutes } from "@app/appRouter.ts";
import { AppScaffold } from "@app/components/AppScaffold.tsx";
import { ModuleGate } from "@app/components/ModuleGate.tsx";
import { Toaster } from "@app/components/shadcn/sonner.tsx";
import { Env } from "@app/Env.ts";
import { useAuthStore } from "@app/stores/useAuthStore.ts";
import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect } from "react";

export const Route = createRootRouteWithContext<{ isAuthenticated: boolean }>()(
  {
    component: RootPage,
  },
);

function RootPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((store) => store.user);
  const redirectPath = getRedirectPath({
    currentPath: pathname,
    isAuthenticated: !!user,
  });

  useEffect(() => {
    if (!redirectPath) {
      return;
    }
    navigate({ to: redirectPath }).catch(() => null);
  }, [navigate, redirectPath]);

  return (
    <>
      <AppScaffold hideNavigationBar={isAuthenticationRoute(pathname)}>
        <ModuleGate pathname={pathname}>
          <Outlet />
        </ModuleGate>
      </AppScaffold>
      <Toaster position="top-center" />
      {Env.MODE === "development" ? (
        <div className="hidden md:block">
          <TanStackRouterDevtools />
        </div>
      ) : null}
    </>
  );
}

function getRedirectPath({
  currentPath,
  isAuthenticated,
}: {
  currentPath: string;
  isAuthenticated: boolean;
}): AppRoutes | null {
  if (isAuthenticated && isAuthenticationRoute(currentPath)) {
    return "/employees";
  }

  if (!isAuthenticated && !isAuthenticationRoute(currentPath)) {
    return "/auth/login";
  }

  return null;
}

function isAuthenticationRoute(pathname: string) {
  return pathname.startsWith("/auth");
}
